import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FormattedRoleDetails } from "../../pages/UserManagement/components/RolesLayout/utils/types";

interface InactiveUserModalData {
  userName: string;
  role: string;
  id: string;
}

interface UserManagementState {
  selectedRole: FormattedRoleDetails | null;
  checkedUserIds: string[];
  userIdForEditOrView: string | null;
  filter: {
    role: string[];
    status: string[];
    client_codes: string[];
  };
  sort: {
    field: string | null;
    order: number | null;
  };
  drawer: {
    open: boolean;
    type: string | null;
  };
  modal: {
    inactiveUserModal: {
      open: boolean;
      data: InactiveUserModalData | null;
    };
  };
}

const initialState: UserManagementState = {
  selectedRole: null,
  checkedUserIds: [],
  userIdForEditOrView: null,
  filter: {
    role: [],
    status: [],
    client_codes: [],
  },
  sort: {
    field: null,
    order: null,
  },
  drawer: {
    open: false,
    type: null,
  },
  modal: {
    inactiveUserModal: {
      open: false,
      data: null,
    },
  },
};

const userManagementSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    setSelectedRole: (state, action: PayloadAction<FormattedRoleDetails>) => {
      state.selectedRole = action.payload as FormattedRoleDetails;
    },
    setRoleFilter: (state, action: PayloadAction<string>) => {
      state.filter.role = [...state.filter.role, action.payload];
    },
    removeRoleFilter: (state, action: PayloadAction<string>) => {
      state.filter.role = state.filter.role.filter(
        (role) => role !== action.payload
      );
    },
    clearRoleFilter: (state) => {
      state.filter.role = [];
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.filter.status = [...state.filter.status, action.payload];
    },
    removeStatusFilter: (state, action: PayloadAction<string>) => {
      state.filter.status = state.filter.status.filter(
        (status) => status !== action.payload
      );
    },
    clearStatusFilter: (state) => {
      state.filter.status = [];
    },
    setClientCodesFilter: (state, action: PayloadAction<string>) => {
      state.filter.client_codes = [
        ...state.filter.client_codes,
        action.payload,
      ];
    },
    removeClientCodesFilter: (state, action: PayloadAction<string>) => {
      state.filter.client_codes = state.filter.client_codes.filter(
        (client_code) => client_code !== action.payload
      );
    },
    clearClientCodesFilter: (state) => {
      state.filter.client_codes = [];
    },
    setUserIdForEditOrView: (state, action: PayloadAction<string>) => {
      state.userIdForEditOrView = action.payload;
    },
    clearUserIdForEditOrView: (state) => {
      state.userIdForEditOrView = null;
    },
    setSort: (
      state,
      action: PayloadAction<{ field: string | null; order: number | null }>
    ) => {
      state.sort = action.payload;
    },
    clearSort: (state) => {
      state.sort = { field: null, order: null };
    },
    clearAllFilters: (state) => {
      state.filter = { role: [], status: [], client_codes: [] };
      state.sort = { field: null, order: null };
      state.selectedRole = null;
    },
    toggleCheckedUser: (state, action: PayloadAction<string>) => {
      const userId = action?.payload;
      const index = state?.checkedUserIds?.indexOf(userId);
      if (index > -1) {
        state.checkedUserIds.splice(index, 1);
      } else {
        state.checkedUserIds.push(userId);
      }
    },
    setAllCheckedUserIds: (state, action: PayloadAction<string[]>) => {
      state.checkedUserIds = action.payload;
    },
    clearAllCheckedUserIds: (state) => {
      state.checkedUserIds = [];
    },
    setDrawer: (
      state,
      action: PayloadAction<{ open: boolean; type: string | null }>
    ) => {
      state.drawer = action.payload;
    },
    clearDrawer: (state) => {
      state.drawer = { open: false, type: null };
    },
    setInactiveUserModal: (state, action: PayloadAction<boolean>) => {
      state.modal.inactiveUserModal.open = action.payload;
    },
    setInactiveUserModalData: (
      state,
      action: PayloadAction<InactiveUserModalData>
    ) => {
      state.modal.inactiveUserModal.data = action.payload;
    },
  },
});

export const {
  setSelectedRole,
  setRoleFilter,
  removeRoleFilter,
  clearRoleFilter,
  setStatusFilter,
  removeStatusFilter,
  clearStatusFilter,
  setClientCodesFilter,
  removeClientCodesFilter,
  clearClientCodesFilter,
  setUserIdForEditOrView,
  clearUserIdForEditOrView,
  setSort,
  clearSort,
  clearAllFilters,
  toggleCheckedUser,
  setAllCheckedUserIds,
  clearAllCheckedUserIds,
  setDrawer,
  clearDrawer,
  setInactiveUserModal,
  setInactiveUserModalData,
} = userManagementSlice.actions;
export default userManagementSlice;
